import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ModalElement, StyledToggleHeading, Typography, Wrapper } from '@pkg/components';
import { useAppDispatch, useModal } from '@pkg/hooks';

import { Admin, type LootBox, LootBoxStatus, type LootBoxType, type PagingParams } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { addNotification } from '@app/store/actions/notification';

import AdminEditLootBox from './AdminEditLootBox/AdminEditLootBox';
import LootBoxMgmt from './LootBoxMgmt';
import LootBoxMgmtTable from './LootBoxMgmtTable';
import LootBoxTypeConfig from './LootBoxTypeConfig';
import LootBoxTypesList from './LootBoxTypesList/LootBoxTypesList';
import './_LootBoxes.scss';

export const LootBoxes = () => {
    const [lootBoxTypes, setLootBoxTypes] = useState<LootBoxType[]>([]);
    const [showConfig, setShowConfig] = useState<boolean>(false);
    const [showGenerate, setShowGenerate] = useState<boolean>(false);
    const [lootBoxes, setLootBoxes] = useState<LootBox[]>([]);
    const [selectedLootBoxId, setSelectedLootBoxId] = useState<string>('');
    const { isShowing, toggle } = useModal();
    const [email, setEmail] = useState<string>('');
    const [ownerId, setOwnerId] = useState<string>('');
    const [lootBoxStatus, setLootBoxStatus] = useState<LootBoxStatus>(LootBoxStatus.Available);
    const [newLootBoxStatus, setNewLootBoxStatus] = useState<LootBoxStatus>(LootBoxStatus.Used);
    const [mounted, setMounted] = useState(true);
    const hasFetchedList = useRef(false);
    const hasFetchedTypeList = useRef(false);
    const dispatch = useAppDispatch();

    const selectedType = useRef('');

    useEffect(() => {
        return () => {
            setMounted(false);
        };
    }, []);

    const getLootBoxTypeList = useCallback(() => {
        apiFactory()
            .call(Admin.LootBoxes.getTypes, {
                query: {
                    sort: 'created_at',
                    dir: 'Desc',
                },
            })
            .then((r) => {
                if (r.ok && r.value && mounted) {
                    setLootBoxTypes(r.value);
                    hasFetchedTypeList.current = true;
                }
            })
            .catch((e) => {
                if (e) {
                    dispatch(
                        addNotification({
                            message: e,
                            severity: 'error',
                        }),
                    );
                }
            });
    }, [mounted, dispatch]);

    const getLootBoxList = useCallback(
        (lootboxTypeId?: string) => {
            const baseQuery = {
                sort: 'created_at',
                dir: 'Asc' as PagingParams['dir'],
                limit: 1000,
            };

            const query = lootboxTypeId
                ? {
                      ...baseQuery,
                      lootbox_type_id: lootboxTypeId,
                  }
                : baseQuery;
            apiFactory()
                .call(Admin.LootBoxes.getLootBoxes, { query })
                .then((r) => {
                    if (r.ok && r.value && r.value.data) {
                        setLootBoxes(r.value.data);
                        hasFetchedList.current = true;
                    }
                })
                .catch((e) => {
                    if (e) {
                        dispatch(
                            addNotification({
                                message: e,
                                severity: 'error',
                            }),
                        );
                    }
                });
        },
        [dispatch],
    );

    useEffect(() => {
        if (!hasFetchedTypeList.current) {
            getLootBoxTypeList();
        }
        if (!hasFetchedList.current) {
            getLootBoxList();
        }
    }, [getLootBoxList, getLootBoxTypeList]);

    const handleLootBoxSelected = (id) => {
        setSelectedLootBoxId(id);
        const selectedLootBox = lootBoxes.find((lb) => lb.id === id);
        if (selectedLootBox) {
            const ownerID = selectedLootBox.owner_id;
            const currentStatus = selectedLootBox.status;
            setOwnerId(ownerID && ownerID.length > 0 ? ownerID : '');
            setLootBoxStatus(currentStatus);
            toggle();
        }
    };

    const deleteLootBox = (id: string) => {
        const deleteConfirm = window.confirm("You're about to delete a loot box!");
        if (id && deleteConfirm) {
            apiFactory()
                .call(Admin.LootBoxes.removeLootBox, {
                    id: id,
                })
                .then((r) => {
                    if (r.ok) {
                        dispatch(addNotification({ message: 'Prism Case Deleted!', severity: 'success' }));
                        getLootBoxList();
                    } else {
                        dispatch(
                            addNotification({ message: r?.error?.detail?.toString() || 'Error', severity: 'error' }),
                        );
                    }
                })
                .catch((e) => {
                    dispatch(addNotification({ message: e.error, severity: 'error' }));
                });
        }
    };

    const updateLootBoxOwner = useCallback(
        (id, owner_email, status) => {
            // if the Prism Case's current status is already an updatable one, this is called to add/update the owner and status
            apiFactory()
                .call(Admin.LootBoxes.updateLootBox, {
                    id,
                    value: {
                        owner_email,
                        status,
                    },
                })
                .then((r) => {
                    if (r.ok) {
                        dispatch(addNotification({ message: 'Prism Case Updated!', severity: 'success' }));
                        getLootBoxList();
                    } else {
                        dispatch(
                            addNotification({ message: r.error?.detail?.toString() || 'Error', severity: 'error' }),
                        );
                    }
                })
                .catch((e) => {
                    dispatch(addNotification({ message: e.error, severity: 'error' }));
                })
                .finally(() => {
                    toggle();
                });
        },
        [dispatch, toggle, getLootBoxList],
    );

    const updateLootBoxStatus = useCallback(
        (id, owner_email, new_status) => {
            // this functions serves as a stepping stone for the Prism Cases whose current status is "Available"
            apiFactory()
                .call(Admin.LootBoxes.updateLootBox, {
                    id,
                    value: {
                        owner_email,
                        status: LootBoxStatus.Owned, // this is hardcoded to "Owned" because either the user wants to update to "Owned" and it ends here,
                        // or they intend for the new status to be "Used" in which case it needs to be "Owned" first
                        // and if successful, it will call updateLootBoxOwner()
                    },
                })
                .then((r) => {
                    if (r.ok) {
                        if (new_status !== LootBoxStatus.Owned) {
                            // ONLY call updateLootBoxOwner() if it needs to be changed to anything but "Owned" (because it already is "Owned" if this was a success)
                            updateLootBoxOwner(id, owner_email, new_status);
                        } else {
                            dispatch(addNotification({ message: 'Prism Case Updated!', severity: 'success' }));
                            toggle();
                        }
                    } else {
                        dispatch(
                            addNotification({ message: r?.error?.detail?.toString() || 'Error', severity: 'error' }),
                        );
                    }
                })
                .catch((e) => {
                    dispatch(addNotification({ message: e.error, severity: 'error' }));
                });
        },
        [dispatch, updateLootBoxOwner, toggle],
    );

    const handleUpdateLootBox = useCallback(
        (id: string) => {
            // we want to assign owners to Prism Cases
            // Prism Cases can only accept owner_email with a status of "Owned" or "Used" ("Used" is an opened loot box, Yats are avail to user)
            // Prism Cases cannot go from status:"Available" to status:"Used" directly (only to "Owned")
            if (lootBoxStatus === LootBoxStatus.Used || lootBoxStatus === LootBoxStatus.Owned) {
                // here the status is already one that allows an owner to be set
                updateLootBoxOwner(id, email, newLootBoxStatus);
            } else {
                // here the status is either "Available" or "Draft", but will likely be "Available" because that is the status from the generator
                updateLootBoxStatus(id, email, newLootBoxStatus);
            }
        },
        [lootBoxStatus, email, newLootBoxStatus, updateLootBoxOwner, updateLootBoxStatus],
    );

    const handleStatusChecked = useCallback((checked) => {
        setNewLootBoxStatus(checked ? LootBoxStatus.Used : LootBoxStatus.Owned);
    }, []);

    function handleFilter(id: string) {
        selectedType.current = id;
        getLootBoxList(id === 'all' ? undefined : id);
    }

    return (
        <Wrapper key={lootBoxes.length} className="admin-lootboxes">
            <StyledToggleHeading type="button" onClick={() => setShowConfig((c) => !c)}>
                <Typography type="headline" size={20}>
                    Prism Case Type Config +
                </Typography>
            </StyledToggleHeading>

            {showConfig && (
                <Wrapper>
                    <LootBoxTypeConfig onTypeSaved={getLootBoxTypeList} />
                    <LootBoxTypesList lootBoxTypes={lootBoxTypes} />
                    <br />
                </Wrapper>
            )}

            <StyledToggleHeading type="button" onClick={() => setShowGenerate((c) => !c)}>
                <Typography type="headline" size={20}>
                    Generate Prism Case +
                </Typography>
            </StyledToggleHeading>

            {showGenerate && <LootBoxMgmt lootBoxTypes={lootBoxTypes} onSuccess={getLootBoxList} />}
            <LootBoxMgmtTable
                lootBoxes={lootBoxes}
                onDelete={(id) => deleteLootBox(id)}
                lootBoxTypes={lootBoxTypes}
                onSelected={handleLootBoxSelected}
                onFilterLootBoxes={handleFilter}
                currentTypeId={selectedType.current}
            />
            <ModalElement isShowing={selectedLootBoxId.length > 0 && isShowing} hide={toggle} isLarge={true}>
                <div className="modal-content">
                    <AdminEditLootBox
                        id={selectedLootBoxId}
                        updateLootBox={handleUpdateLootBox}
                        onEmailSet={(email) => setEmail(email)}
                        ownerId={ownerId}
                        onCancel={toggle}
                        onChecked={(checked) => {
                            handleStatusChecked(checked);
                        }}
                    />
                </div>
            </ModalElement>
        </Wrapper>
    );
};
